import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import HomeworkViewer from './HomeworkViewer';
import ClassroomSelector from './components/ClassroomSelector';
import HomeworksBank from './components/HomeworksBank';
import { buildApiUrl } from './config';

const CreateHomeworkComponent = ({ onSubmit, assignedForms = [], teacherName, editingHomework, onEditComplete }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [homeworkTitle, setHomeworkTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [showHomeworkBank, setShowHomeworkBank] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ 
    question: "", 
    type: "single", 
    options: ["", "", "", ""], 
    correct: "", 
    image: null,
    videoUrl: "",
    optionImages: ["", "", "", ""]
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Load saved drafts when component mounts
    loadSavedDrafts();
  }, []);

  // ✅ NEW: Load editing homework data
  useEffect(() => {
    if (editingHomework) {
      console.log('📝 Loading homework for editing:', editingHomework);
      
      // Populate form fields with existing homework data
      setHomeworkTitle(editingHomework.title || '');
      setSubject(editingHomework.subject || '');
      setDueDate(editingHomework.dueDate || '');
      
      // Handle both single class (legacy) and multiple classes
      const assignedTo = editingHomework.assignedTo || [];
      if (Array.isArray(assignedTo)) {
        setSelectedClasses(assignedTo);
        setSelectedClass(assignedTo[0] || ''); // Keep for backward compatibility
      } else {
        setSelectedClasses([assignedTo]);
        setSelectedClass(assignedTo);
      }
      
      // Load questions if they exist
      if (editingHomework.questions && editingHomework.questions.length > 0) {
        setGeneratedQuestions(editingHomework.questions);
        setStep(2); // Go to review step
      } else {
        setStep(1); // Start at creation step
      }
      
      // Set prompt if it exists (from AI generated homework)
      if (editingHomework.prompt) {
        setPrompt(editingHomework.prompt);
      }
    }
  }, [editingHomework]);

  // ✅ ADD: Check for draft data passed from navigation state
  useEffect(() => {
    if (location.state?.loadFromDraft && location.state?.draftData) {
      const draftData = location.state.draftData;
      console.log('Loading draft data:', draftData);
      
      // Load the draft data into the form
      setPrompt(draftData.prompt || '');
      setGeneratedQuestions(draftData.questions || []);
      
      // Move to step 2 (review) since questions are already generated
      if (draftData.questions && draftData.questions.length > 0) {
        setStep(2);
      }
    }
  }, [location.state]);

  // ✅ ADD: Check for homework bank data passed from navigation state
  useEffect(() => {
    if (location.state?.loadFromBank && location.state?.homeworkData) {
      const bankData = location.state.homeworkData;
      console.log('Loading homework from bank:', bankData);
      
      // Load the homework data into the form
      setHomeworkTitle(bankData.title || '');
      setSubject(bankData.subject || '');
      setDueDate(''); // Clear due date for new assignment
      setGeneratedQuestions(bankData.questions || []);
      
      // Clear assigned classes since this is a new assignment
      setSelectedClasses([]);
      setSelectedClass('');
      
      // Always go to step 2 (Review Questions) when loading from homework bank
      setStep(2);
    }
  }, [location.state]);

  const loadSavedDrafts = async () => {
    try {
      const response = await fetch(`${buildApiUrl("")}/saved-homework-drafts`);
      if (response.ok) {
        const data = await response.json();
        const drafts = data.drafts || [];
        setSavedDrafts(Array.isArray(drafts) ? drafts : []);
      }
    } catch (error) {
      console.error('Error loading saved drafts:', error);
    }
  };

  const saveForLater = async () => {
    try {
      const draftData = {
        prompt: prompt,
        questions: generatedQuestions,
        createdBy: localStorage.getItem('teacherEmail') || 'teacher'
      };

      const response = await fetch(buildApiUrl("/save-homework-draft"), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(draftData),
      });

      if (response.ok) {
        alert('✅ Homework saved for later!');
        loadSavedDrafts(); // Refresh drafts list
      } else {
        alert('❌ Failed to save homework');
      }
    } catch (error) {
      console.error('Error saving homework:', error);
      alert('❌ Error saving homework');
    }
  };

  // ✅ NEW: Function to handle loading homework from bank
  const loadFromHomeworkBank = (homeworkData) => {
    console.log('📚 Loading homework from bank:', homeworkData);
    
    // Ensure we stay on the create tab by updating the URL
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('tab', 'create');
    window.history.replaceState({}, '', currentUrl);
    
    // Load the homework data into the form
    setHomeworkTitle(homeworkData.title || '');
    setSubject(homeworkData.subject || '');
    setPrompt(homeworkData.prompt || '');
    setGeneratedQuestions(homeworkData.questions || []);
    
    // Reset due date and assignments for new assignment
    setDueDate('');
    setSelectedClasses([]);
    setSelectedClass('');
    
    // Always go to step 2 (Review Questions) when loading from homework bank
    setStep(2);
    
    // Hide the homework bank
    setShowHomeworkBank(false);
    
    alert(`✅ Loaded homework: "${homeworkData.title}" - Review the questions and assign to new classes!`);
  };

  // Helper function to convert video URLs to embeddable format
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    
    // Vimeo
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    
    // Already an embed URL
    if (url.includes('/embed/')) {
      return url;
    }
    
    return null;
  };

  const addOption = () => {
    setNewQuestion({
      ...newQuestion,
      options: [...(newQuestion.options || []), ""]
    });
  };

  const updateOption = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const removeOption = (index) => {
    const updatedOptions = newQuestion.options.filter((_, i) => i !== index);
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const handleImageUpload = async (e, type = 'question', optionIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📸 Uploading image:', file.name, 'Type:', type, 'Option index:', optionIndex);
    setUploadingImage(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${buildApiUrl("")}/upload-question-image`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      console.log('📸 Image upload response:', data);
      
      if (data.url) {
        // Convert relative URL to full URL using the backend server
        const fullImageUrl = data.url.startsWith('http') ? data.url : `${buildApiUrl("")}${data.url}`;
        
        if (type === 'question') {
          setNewQuestion({ ...newQuestion, image: fullImageUrl });
        } else if (type === 'option' && optionIndex !== null) {
          const newOptionImages = [...newQuestion.optionImages];
          newOptionImages[optionIndex] = fullImageUrl;
          setNewQuestion({ ...newQuestion, optionImages: newOptionImages });
        }
        
        console.log('📸 Full image URL set:', fullImageUrl);
      } else {
        console.error('📸 No URL in response:', data);
        alert("Failed to upload image - no URL returned");
      }
    } catch (err) {
      console.error('📸 Image upload error:', err);
      alert("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const addQuestion = () => {
    if (!newQuestion.question || !newQuestion.correct) {
      alert("Please provide both the question and the correct answer.");
      return;
    }
    
    const name = "q" + (generatedQuestions.length + 1);
    const newQ = { 
      ...newQuestion, 
      name,
      label: newQuestion.question, // For compatibility with existing code
      question: newQuestion.question // Ensure both properties are set
    };
    
    console.log('➕ Adding question with media:', {
      hasImage: !!newQ.image,
      imageUrl: newQ.image,
      hasVideo: !!newQ.videoUrl,
      videoUrl: newQ.videoUrl,
      optionImagesCount: newQ.optionImages.filter(img => img).length,
      optionImages: newQ.optionImages
    });
    
    setGeneratedQuestions([...generatedQuestions, newQ]);
    setNewQuestion({ 
      question: "", 
      type: "single", 
      options: ["", "", "", ""], 
      correct: "", 
      image: null,
      videoUrl: "",
      optionImages: ["", "", "", ""]
    });
  };

  const [editingIndex, setEditingIndex] = useState(-1);

  const editQuestion = (index, updatedQuestion) => {
    const updatedQuestions = [...generatedQuestions];
    updatedQuestions[index] = updatedQuestion;
    setGeneratedQuestions(updatedQuestions);
    setEditingIndex(-1); // Exit edit mode
  };

  const startEditingQuestion = (index) => {
    setEditingIndex(index);
  };

  const deleteQuestion = (index) => {
    const updatedQuestions = generatedQuestions.filter((_, i) => i !== index);
    setGeneratedQuestions(updatedQuestions);
  };

  const generateQuestions = () => {
    if (generatedQuestions.length === 0) {
      alert("Please add at least one question before proceeding.");
      return;
    }
    setStep(2);
  };

  const assignHomework = async () => {
    console.log("🚀 Starting assignHomework function, current step:", step);
    setIsLoading(true);
    try {
      const teacherEmail = localStorage.getItem("teacherEmail");
      const createdAt = editingHomework ? editingHomework.createdAt : new Date().toISOString();
      
      console.log("👤 Teacher email from localStorage:", teacherEmail);
      console.log("✏️ Editing mode:", !!editingHomework);
      
      const payload = {
        ...(editingHomework && { id: editingHomework.id }), // Include ID if editing
        title: homeworkTitle,
        createdAt,
        subject: subject || "Custom",
        assignedTo: selectedClasses.length > 0 ? selectedClasses : [selectedClass], // Use selectedClasses first, fallback to selectedClass
        questions: generatedQuestions,
        createdBy: teacherEmail,
        visible: true,
        dueDate,
        attachments: editingHomework?.attachments || [],
        term: editingHomework?.term || "Term 1"
      };

      if (editingHomework) {
        console.log("📝 Updating existing homework...", JSON.stringify(payload, null, 2));
        
        // Update existing homework
        const updateRes = await fetch(buildApiUrl(`/homework/update/${editingHomework.id}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        
        if (!updateRes.ok) {
          const errorText = await updateRes.text();
          throw new Error(`Failed to update homework: ${updateRes.status} - ${errorText}`);
        }
        
        console.log("✅ Homework successfully updated!");
        alert("✅ Homework updated successfully!");
        
        // Call edit complete callback
        if (onEditComplete) {
          onEditComplete();
        }
        
      } else {
        console.log("📝 Assigning new homework directly...", JSON.stringify(payload, null, 2));

        // Directly assign homework without saving to drafts first
        const assignRes = await fetch(buildApiUrl("/assign-homework-per-student"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        
        console.log("📝 Assignment response status:", assignRes.status);
        
        if (!assignRes.ok) {
          const errorText = await assignRes.text();
          console.error("❌ Assignment failed with status:", assignRes.status);
          console.error("❌ Assignment error text:", errorText);
          throw new Error(`Failed to assign homework: ${assignRes.status} - ${errorText}`);
        }
        
        let assignData;
        try {
          assignData = await assignRes.json();
          console.log("📝 Assignment response data:", JSON.stringify(assignData, null, 2));
        } catch (parseError) {
          console.error("❌ Failed to parse assignment response as JSON:", parseError);
          // Continue anyway since the homework was likely assigned successfully
          assignData = { message: "Assignment completed (JSON parse error)" };
        }
        
        console.log("✅ Homework successfully assigned!");
        alert("✅ Homework assigned successfully!");
        console.log("📋 About to set step to 4...");
        setStep(4);
        console.log("📋 Step set to 4 completed");
      }
    } catch (error) {
      console.error('❌ Error with homework:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error details:', {
        message: error.message,
        name: error.name,
        editingMode: !!editingHomework
      });
      alert(`Failed to ${editingHomework ? 'update' : 'assign'} homework: ${error.message}`);
    } finally {
      console.log("🔄 Finally block - setting isLoading to false");
      setIsLoading(false);
    }
  };

  // NEW: Step navigation functions
  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);
  const resetToStep1 = () => setStep(1);

  // NEW: Render based on current step
  if (step === 1) {
    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-red-600 bg-clip-text text-transparent">
              Create Custom Homework
            </h2>
            <p className="text-sm text-gray-600 mt-1">Build your homework question by question</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHomeworkBank(!showHomeworkBank)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
            >
              <span className="text-lg">🏦</span>
              <span className="text-sm">{showHomeworkBank ? 'Hide' : 'Load from'} Bank</span>
            </button>
            {savedDrafts.length > 0 && (
              <button
                onClick={() => setShowDrafts(!showDrafts)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <span className="text-lg">💾</span>
                <span className="text-sm">{showDrafts ? 'Hide' : 'Show'} Drafts ({savedDrafts.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* Saved Drafts Section */}
        {showDrafts && savedDrafts.length > 0 && (
          <div className="p-5 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border-2 border-gray-200/50 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">💾</span>
              Saved Drafts
            </h3>
            <div className="grid gap-3">
              {savedDrafts.map((draft, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{draft.prompt || 'Untitled Draft'}</p>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <span>❓</span>
                        {draft.questions?.length || 0} questions
                      </span>
                      <span className="flex items-center gap-1">
                        <span>📅</span>
                        {new Date(draft.created_at).toLocaleDateString()}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setPrompt(draft.prompt || '');
                      setGeneratedQuestions(draft.questions || []);
                      if (draft.questions?.length > 0) setStep(2);
                      setShowDrafts(false);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Load
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Homework Bank Section */}
        {showHomeworkBank && (
          <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200/50 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">🏦</span>
              Load from Homework Bank
            </h3>
            <HomeworksBank onSelectHomework={loadFromHomeworkBank} />
          </div>
        )}

        {/* Step 1: Create Questions */}
        <div className="space-y-6">
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <span>📝</span>
              Homework Description (Optional)
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what this homework is about..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
          </div>

          {/* Question Creation Form */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span>❓</span>
              Add Questions
            </h3>
            
            <div className="space-y-4 bg-gradient-to-r from-yellow-50 to-blue-50 p-6 rounded-xl border-2 border-yellow-200/50">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question</label>
                <textarea
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                  placeholder="Enter your question..."
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Question Type</label>
                <select
                  value={newQuestion.type}
                  onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="single">Single Choice</option>
                  <option value="multiple">Multiple Choice</option>
                  <option value="text">Text Answer</option>
                </select>
              </div>

              {(newQuestion.type === "single" || newQuestion.type === "multiple") && (
                <div>
                  <label className="block text-sm font-medium mb-3 font-semibold text-gray-800">Options</label>
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="mb-4 p-4 border-2 border-blue-200 rounded-lg bg-gradient-to-r from-white to-blue-50">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{index + 1}</span>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {newQuestion.options.length > 2 && (
                          <button
                            onClick={() => removeOption(index)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="ml-8">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Option {index + 1} Image (Optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'option', index)}
                          disabled={uploadingImage}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {newQuestion.optionImages[index] && (
                          <div className="mt-2">
                            <img src={newQuestion.optionImages[index]} alt={`Option ${index + 1}`} className="max-h-24 rounded-lg border border-gray-200" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={addOption}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 py-2 rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
                  >
                    + Add Option
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Correct Answer</label>
                {newQuestion.type === "text" ? (
                  <input
                    type="text"
                    value={newQuestion.correct}
                    onChange={(e) => setNewQuestion({ ...newQuestion, correct: e.target.value })}
                    placeholder="Enter the correct answer..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <select
                    value={newQuestion.correct}
                    onChange={(e) => setNewQuestion({ ...newQuestion, correct: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select correct answer...</option>
                    {newQuestion.options.filter(opt => opt.trim()).map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Question Media Section */}
              <div className="space-y-4 border-t pt-4 mt-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span>🎨</span>
                  Question Media (Optional)
                </h4>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Question Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'question')}
                    disabled={uploadingImage}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {uploadingImage && <p className="text-sm text-blue-600 mt-1">Uploading image...</p>}
                  {newQuestion.image && (
                    <div className="mt-2">
                      <img src={newQuestion.image} alt="Question" className="max-w-xs rounded-lg border-2 border-gray-200" />
                      <button
                        onClick={() => setNewQuestion({ ...newQuestion, image: null })}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Video URL (YouTube, Vimeo, etc.)</label>
                  <input
                    type="url"
                    value={newQuestion.videoUrl || ''}
                    onChange={(e) => setNewQuestion({ ...newQuestion, videoUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {newQuestion.videoUrl && (
                    <div className="mt-2">
                      {getEmbedUrl(newQuestion.videoUrl) ? (
                        <div>
                          <iframe
                            src={getEmbedUrl(newQuestion.videoUrl)}
                            className="w-full max-w-md h-48 rounded-lg border-2 border-purple-200"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Video preview"
                          />
                          <button
                            onClick={() => setNewQuestion({ ...newQuestion, videoUrl: '' })}
                            className="mt-2 text-sm text-red-600 hover:text-red-800"
                          >
                            Remove Video
                          </button>
                        </div>
                      ) : (
                        <div className="p-2 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-xs text-yellow-800">⚠️ Video URL format not recognized. Try a direct YouTube or Vimeo link.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={addQuestion}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2"
              >
                <span>➕</span>
                Add Question
              </button>
            </div>
          </div>

          {/* Added Questions List */}
          {generatedQuestions.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>📋</span>
                Added Questions ({generatedQuestions.length})
              </h3>
              <div className="space-y-3">
                {generatedQuestions.map((q, index) => (
                  <div key={index} className="p-4 border-2 border-blue-200 rounded-xl bg-gradient-to-r from-white to-yellow-50 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <span className="w-7 h-7 bg-gradient-to-r from-blue-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          Question {index + 1}
                        </h4>
                        <p className="text-gray-800 mb-2 font-medium">{q.question || q.label}</p>
                        
                        {/* Question Media */}
                        {q.image && (
                          <div className="mb-2">
                            <img src={q.image} alt="Question" className="max-w-xs rounded-lg border border-gray-200" />
                          </div>
                        )}
                        {q.videoUrl && (
                          <div className="mb-2">
                            {getEmbedUrl(q.videoUrl) ? (
                              <iframe
                                src={getEmbedUrl(q.videoUrl)}
                                className="w-full max-w-md h-48 rounded-lg border-2 border-purple-200"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Question video"
                              />
                            ) : (
                              <div className="p-2 bg-purple-50 rounded border border-purple-200">
                                <p className="text-xs text-purple-800">📹 Video: {q.videoUrl}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {q.type !== "text" && (
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><span className="font-semibold">Options:</span></p>
                            <div className="pl-4 space-y-1">
                              {q.options?.map((opt, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span>{opt}</span>
                                  {q.optionImages && q.optionImages[idx] && (
                                    <img 
                                      src={q.optionImages[idx]} 
                                      alt={`Option ${idx + 1}`} 
                                      className="h-8 w-8 object-cover rounded border" 
                                      onError={(e) => {
                                        console.error('❌ Failed to load preview option image:', q.optionImages[idx]);
                                        e.target.style.display = 'none';
                                      }}
                                      onLoad={() => console.log('✅ Preview option image loaded:', q.optionImages[idx])}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                            <p><span className="font-semibold">Correct:</span> <span className="font-medium text-green-600">{q.correct}</span></p>
                          </div>
                        )}
                        {q.type === "text" && (
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Correct Answer:</span> <span className="font-medium text-green-600">{q.correct}</span>
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => deleteQuestion(index)}
                        className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-lg text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t">
            <div></div>
            <button
              onClick={generateQuestions}
              disabled={generatedQuestions.length === 0}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2 ${
                generatedQuestions.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-red-500 hover:from-blue-600 hover:to-red-600 text-white'
              }`}
            >
              <span>Next: Review Questions</span>
              <span>➡️</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-red-600 bg-clip-text text-transparent flex items-center gap-2">
              <span>🔍</span>
              Review Questions
            </h2>
            <p className="text-sm text-gray-600 mt-1">Preview and edit your questions before assigning</p>
          </div>
          <h2 className="text-2xl font-bold">Review Questions</h2>
          <div className="flex gap-3">
            <button
              onClick={saveForLater}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded transition-colors"
            >
              Save for Later
            </button>
          </div>
        </div>

        {/* Question Preview */}
        <div className="space-y-4">
          {generatedQuestions.map((q, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-6 bg-white">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Question {index + 1}</h3>
                <div className="flex gap-2">
                  {editingIndex !== index && (
                    <button
                      onClick={() => startEditingQuestion(index)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => deleteQuestion(index)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {editingIndex === index ? (
                // Edit mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Question</label>
                    <textarea
                      value={q.question || q.label}
                      onChange={(e) => {
                        const updated = { ...q, question: e.target.value, label: e.target.value };
                        editQuestion(index, updated);
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      rows="2"
                    />
                  </div>
                  
                  {q.type !== "text" && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Options</label>
                      {q.options?.map((option, optIndex) => (
                        <div key={optIndex} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...q.options];
                              newOptions[optIndex] = e.target.value;
                              const updated = { ...q, options: newOptions };
                              editQuestion(index, updated);
                            }}
                            className="flex-1 p-2 border border-gray-300 rounded"
                            placeholder={`Option ${optIndex + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Correct Answer</label>
                    {q.type === "text" ? (
                      <input
                        type="text"
                        value={q.correct}
                        onChange={(e) => {
                          const updated = { ...q, correct: e.target.value };
                          editQuestion(index, updated);
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    ) : (
                      <select
                        value={q.correct}
                        onChange={(e) => {
                          const updated = { ...q, correct: e.target.value };
                          editQuestion(index, updated);
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select correct answer...</option>
                        {q.options?.filter(opt => opt.trim()).map((option, optIndex) => (
                          <option key={optIndex} value={option}>{option}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setEditingIndex(-1)}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                  >
                    Done Editing
                  </button>
                </div>
              ) : (
                // View mode
                <div>
                  <div className="mb-4">
                    <p className="text-gray-900 mb-2 font-medium">{q.question || q.label}</p>
                    {q.image && (
                      <div className="mb-3">
                        <img src={q.image} alt="Question" className="max-w-md rounded-lg border-2 border-gray-200" />
                      </div>
                    )}
                    {q.videoUrl && (
                      <div className="mb-3">
                        {getEmbedUrl(q.videoUrl) ? (
                          <iframe
                            src={getEmbedUrl(q.videoUrl)}
                            className="w-full max-w-2xl h-64 rounded-lg border-2 border-purple-200"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Question video"
                          />
                        ) : (
                          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-sm text-purple-800 font-medium">📹 Video: {q.videoUrl}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {q.type === "text" ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Type: Text Answer</p>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700">Sample answer area...</p>
                      </div>
                      <p className="text-sm text-green-600 mt-2">
                        <strong>Correct Answer:</strong> {q.correct}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Type: {q.type === "single" ? "Single Choice" : "Multiple Choice"}</p>
                      <div className="space-y-2">
                        {q.options?.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-start gap-2 p-2 rounded hover:bg-gray-50">
                            <input
                              type={q.type === "single" ? "radio" : "checkbox"}
                              name={`question-${index}`}
                              disabled
                              className="text-blue-600 mt-1"
                            />
                            <div className="flex-1">
                              <span className={`${option === q.correct ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                                {option}
                                {option === q.correct && " ✓"}
                              </span>
                              {q.optionImages && q.optionImages[optIndex] && (
                                <div className="mt-1">
                                  <img 
                                    src={q.optionImages[optIndex]} 
                                    alt={`Option ${optIndex + 1}`} 
                                    className="max-h-24 rounded border border-gray-200" 
                                    onError={(e) => {
                                      console.error('❌ Failed to load step 2 option image:', q.optionImages[optIndex]);
                                      e.target.style.display = 'none';
                                    }}
                                    onLoad={() => console.log('✅ Step 2 option image loaded:', q.optionImages[optIndex])}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-between pt-6 border-t mt-6">
          <button
            onClick={prevStep}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
          >
            Back: Edit Questions
          </button>
          <button
            onClick={nextStep}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
          >
            Next: Assign Homework
          </button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 min-h-[600px]">
        <h2 className="text-2xl font-bold mb-6">Assign Homework</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-lg font-medium mb-2">Homework Title</label>
            <input
              type="text"
              value={homeworkTitle}
              onChange={(e) => setHomeworkTitle(e.target.value)}
              placeholder="Enter homework title..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-lg font-medium mb-2">Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a subject</option>
              <option value="Math">Math</option>
              <option value="Science">Science</option>
              <option value="Geography">Geography</option>
              <option value="English">English</option>
              <option value="Literacy">Literacy</option>
              <option value="ICT">ICT</option>
            </select>
          </div>

          <div>
            <label className="block text-lg font-medium mb-2">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <ClassroomSelector
            selectedClasses={selectedClasses}
            onSelectionChange={setSelectedClasses}
            assignedForms={assignedForms}
            label="Assign to Classes"
            multiSelect={true}
          />

          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Assignment Summary</h3>
            <p><strong>Questions:</strong> {generatedQuestions.length}</p>
            <p><strong>Title:</strong> {homeworkTitle || 'Not set'}</p>
            <p><strong>Classes:</strong> {
              selectedClasses.length > 0 
                ? selectedClasses.join(', ') 
                : (selectedClass || 'Not selected')
            }</p>
            <p><strong>Due Date:</strong> {dueDate || 'Not set'}</p>
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t">
          <button
            onClick={prevStep}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
          >
            Back: Review
          </button>
          <button
            onClick={assignHomework}
            disabled={isLoading || !homeworkTitle || !subject || (selectedClasses.length === 0 && !selectedClass) || !dueDate}
            className={`px-8 py-3 rounded-lg font-medium ${
              isLoading || !homeworkTitle || !subject || (selectedClasses.length === 0 && !selectedClass) || !dueDate
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {isLoading 
              ? (editingHomework ? 'Updating...' : 'Assigning...') 
              : (editingHomework ? 'Update Homework' : 'Assign Homework')
            }
          </button>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 min-h-[600px] text-center">
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {editingHomework ? 'Homework Updated!' : 'Homework Created Successfully!'}
          </h2>
          
          <p className="text-lg text-gray-600 mb-6">
            {editingHomework 
              ? 'Your homework has been updated and is now available to students.'
              : 'Your homework has been created and assigned to students.'
            }
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2">Assignment Details:</h3>
            <p><strong>Title:</strong> {homeworkTitle}</p>
            <p><strong>Subject:</strong> {subject || 'Not specified'}</p>
            <p><strong>Classes:</strong> {
              selectedClasses.length > 0 
                ? selectedClasses.join(', ') 
                : (selectedClass || 'None selected')
            }</p>
            <p><strong>Due Date:</strong> {dueDate}</p>
            <p><strong>Questions:</strong> {generatedQuestions.length}</p>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={resetToStep1}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg"
            >
              Create Another Homework
            </button>
            <button
              onClick={() => {
                if (editingHomework && onEditComplete) {
                  onEditComplete();
                } else {
                  window.location.href = '/teacher?mainTab=assignments&tab=my-assignments&module=assignments';
                }
              }}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CreateHomeworkComponent;
